/**
 * Cover hash + folder path fields from the in-memory album index row.
 * Detail views inherit these so hero art resolves via the same IPC path as the wall.
 */

import { normalizeCoverHash } from './coverArtIPC';
import { findLocalAlbumIndexRow } from '../services/libraryAlbumIndex';
import type { AlbumIndexRow } from '../services/libraryAlbumIndex';

export type LocalAlbumCoverHints = {
  embedded_cover_hash?: string | null;
  folder_cover_hash?: string | null;
  external_cover_hash?: string | null;
  cover_art_thumb_hash?: string | null;
  sample_file_path?: string | null;
  library_folder_path?: string | null;
};

export function pickCoverHintsFromRow(
  row: AlbumIndexRow | Record<string, unknown> | null | undefined,
): LocalAlbumCoverHints {
  if (!row) return {};
  return {
    embedded_cover_hash: (row.embedded_cover_hash as string) ?? null,
    folder_cover_hash: (row.folder_cover_hash as string) ?? null,
    external_cover_hash: (row.external_cover_hash as string) ?? null,
    cover_art_thumb_hash: (row.cover_art_thumb_hash as string) ?? null,
    sample_file_path: (row.sample_file_path as string) ?? null,
    library_folder_path: (row.library_folder_path as string) ?? null,
  };
}

export function findIndexCoverHintsForAlbum(albumKey?: string | null): LocalAlbumCoverHints {
  return pickCoverHintsFromRow(findLocalAlbumIndexRow(albumKey));
}

export function indexHintsHaveCoverHash(hints: LocalAlbumCoverHints): boolean {
  return !!(
    normalizeCoverHash(hints.embedded_cover_hash)
    || normalizeCoverHash(hints.folder_cover_hash)
    || normalizeCoverHash(hints.external_cover_hash)
  );
}

/** Merge index / navigation cover fields onto an album record (IPC detail may omit hashes). */
export function mergeLocalAlbumCoverHints<T extends Record<string, unknown>>(
  album: T,
  ...sources: Array<LocalAlbumCoverHints | Record<string, unknown> | null | undefined>
): T {
  const merged = { ...album };
  const fields: Array<keyof LocalAlbumCoverHints> = [
    'embedded_cover_hash',
    'folder_cover_hash',
    'external_cover_hash',
    'cover_art_thumb_hash',
    'sample_file_path',
    'library_folder_path',
  ];
  for (const field of fields) {
    const current = merged[field as keyof T];
    if (current != null && String(current).trim() !== '') continue;
    for (const src of sources) {
      if (!src) continue;
      const val = (src as LocalAlbumCoverHints)[field];
      if (val != null && String(val).trim() !== '') {
        (merged as Record<string, unknown>)[field] = val;
        break;
      }
    }
  }
  return merged;
}
